import React from 'react';
import { Box, Text, VStack, Progress, HStack } from '@chakra-ui/react';
import ListComponent from './ListComponent';
import SpacerComponent from './SpacerComponent';
import HeaderComponent from './HeaderComponent';

const HealthScoreSummaryComponent = ({
  header,
  overallScore,
  scoreSummary,
  actionPlan,
  marginTop,
  marginBottom,
  containerVariant
}) => {
  const hasValidScore = overallScore && overallScore.value !== null && !overallScore.isDataMissing;
  const isDataMissing = overallScore && overallScore.isDataMissing;
  
  // Map score to color based on ranges (like the design shows)
  const getProgressColor = (score) => {
    if (score >= 67) return '#62B524'; // Green for 67-100
    if (score >= 33) return '#FFB349'; // Yellow/Orange for 33-66
    return '#FF8D83'; // Red for 0-32
  };
  
  const progressColor = hasValidScore ? getProgressColor(overallScore.progressWidth) : '#D9D9D9';

  return (
    <Box>
      {/* Main Header */}
      <Text
        fontFamily={`var(--${header?.variant || 'headline-md-mid'}-font-family, Sora)`}
        fontWeight={`var(--${header?.variant || 'headline-md-mid'}-font-weight, 800)`}
        fontSize={`var(--${header?.variant || 'headline-md-mid'}-font-size, 24px)`}
        lineHeight={`var(--${header?.variant || 'headline-md-mid'}-line-height, 120%)`}
        letterSpacing={`var(--${header?.variant || 'headline-md-mid'}-letter-spacing, 0px)`}
        color="var(--Dusky-Teal, #203433)"
        mb={4}
      >
        {header?.text || "Here's your health score"}
      </Text>

      {/* Main Container */}
      <Box
        px="var(--chakra-space-8)"
        py="var(--chakra-space-2)"
        gap={`var(--${containerVariant || 'container-card-purple-light'}-gap, 24px)`}
        borderRadius={`var(--${containerVariant || 'container-card-purple-light'}-border-radius, 6px)`}
        opacity={`var(--${containerVariant || 'container-card-purple-light'}-opacity, 1)`}
        bgColor={`var(--${containerVariant || 'container-card-purple-light'}-bg-color, #F4EEFD)`}
        mt={marginTop || 4}
        mb={marginBottom || 6}
      >
        <VStack spacing={6} align="stretch">
          {/* Overall Score Section */}
          <Box textAlign="left">
            {/* Overall Label */}
                         <Text
               fontFamily={`var(--${overallScore.title?.variant || 'headline-sm-mid'}-font-family, Sora)`}
               fontWeight={`var(--${overallScore.title?.variant || 'headline-sm-mid'}-font-weight, 600)`}
               fontSize={`var(--${overallScore.title?.variant || 'headline-sm-mid'}-font-size, 20px)`}
               lineHeight={`var(--${overallScore.title?.variant || 'headline-sm-mid'}-line-height, 120%)`}
               letterSpacing={`var(--${overallScore.title?.variant || 'headline-sm-mid'}-letter-spacing, 0px)`}
               color={overallScore.title?.color || `var(--${overallScore.title?.variant || 'headline-sm-mid'}-color, #5F1B93)`}
               mb={2}
             >
              {overallScore.title?.text || "Overall"}
            </Text>

            {/* Score Display */}
            { hasValidScore && <Box>
                <Text
                  as="span"
                  fontFamily={`var(--${overallScore.score?.variant || 'headline-3xl-mid'}-font-family, Sora)`}
                  fontWeight={`var(--${overallScore.score?.variant || 'headline-3xl-mid'}-font-weight, 800)`}
                  fontSize={`var(--${overallScore.score?.variant || 'headline-3xl-mid'}-font-size, 56px)`}
                  lineHeight={`var(--${overallScore.score?.variant || 'headline-3xl-mid'}-line-height, 120%)`}
                  letterSpacing={`var(--${overallScore.score?.variant || 'headline-3xl-mid'}-letter-spacing, -1px)`}
                  color={`var(--${overallScore.score?.variant || 'headline-3xl-mid'}-color, #5F1B93)`}
                >
                  {overallScore.value}
                </Text>
                <Text
                  as="span"
                  fontFamily={`var(--${overallScore.scoreSuffix?.variant || 'headline-reg'}-font-family, Sora)`}
                  fontWeight={`var(--${overallScore.scoreSuffix?.variant || 'headline-reg'}-font-weight, 400)`}
                  fontSize={`var(--${overallScore.scoreSuffix?.variant || 'headline-reg'}-font-size, 16px)`}
                  lineHeight={`var(--${overallScore.scoreSuffix?.variant || 'headline-reg'}-line-height, 150%)`}
                  letterSpacing={`var(--${overallScore.scoreSuffix?.variant || 'headline-reg'}-letter-spacing, 0px)`}
                  color={`var(--${overallScore.scoreSuffix?.variant || 'headline-reg'}-color, var(--Dusky-Teal, #203433))`}
                  ml={overallScore.scoreSuffix?.marginLeft || "16px"}
                >
                  {overallScore.scoreSuffix?.text || "/100"}
                </Text>
              </Box>
            }

            {/* Progress Bar */}
            { hasValidScore && <Box mt={4}>
              <Progress
                value={hasValidScore ? overallScore.progressWidth : 0}
                role="progressbar"
                aria-valuenow={hasValidScore ? overallScore.progressWidth : 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-labelledby={`Overall score is ${Math.round(overallScore.progressWidth || 0)}%`}
                aria-label={`Overall score is ${Math.round(overallScore.progressWidth || 0)}%`}
                size={overallScore.progressSize || 'sm'}
                style={{
                  backgroundColor: '#D9D9D9',
                  borderRadius: '8px'
                }}
                sx={{
                  '& > div': {
                    backgroundColor: progressColor,
                    borderRadius: '8px'
                  }
                }}
              />
            </Box>}


            {/* Description */}
             <Text
               fontFamily={`var(--${overallScore.description?.variant || 'body-xs-min-reg'}-font-family, Roboto)`}
               fontWeight={`var(--${overallScore.description?.variant || 'body-xs-min-reg'}-font-weight, 400)`}
               fontSize={`var(--${overallScore.description?.variant || 'body-xs-min-reg'}-font-size, 14px)`}
               lineHeight={`var(--${overallScore.description?.variant || 'body-xs-min-reg'}-line-height, 16px)`}
               letterSpacing={`var(--${overallScore.description?.variant || 'body-xs-min-reg'}-letter-spacing, 0.5px)`}
               color={`var(--${overallScore.description?.variant || 'body-xs-min-reg'}-color, var(--Dusky-Teal, #203433))`}
               mt={3}
               wordBreak="break-word"
               overflowWrap="break-word"
               maxWidth="100%"
             >
               {overallScore.description?.text || "The closer to 100, the better it is for our bodies."}
             </Text>
          </Box>

          {/* Score Summary Section */}
          {scoreSummary && (
            <Box>
                             {scoreSummary.icon ? (
                                   <HStack spacing={2} align="center">
                                         {scoreSummary.icon.shape === 'circle' ? (
                       <Box
                         width={scoreSummary.icon.size || "21px"}
                         height={scoreSummary.icon.size || "21px"}
                         bg={scoreSummary.icon.bgColor || "#5F1B93"}
                         borderRadius="50%"
                         transform={`rotate(${scoreSummary.icon.rotation || "0deg"})`}
                         mb={scoreSummary.icon.marginBottom || 0}
                         flexShrink={0}
                       />
                                         ) : scoreSummary.icon.shape === 'triangle' ? (
                       <Box
                         width={0}
                         height={0}
                         borderLeft={`${parseInt(scoreSummary.icon.size || "21") / 2}px solid transparent`}
                         borderRight={`${parseInt(scoreSummary.icon.size || "21") / 2}px solid transparent`}
                         borderBottom={`${scoreSummary.icon.size || "21px"} solid ${scoreSummary.icon.bgColor || "#5F1B93"}`}
                         transform={`rotate(${scoreSummary.icon.rotation || "0deg"})`}
                         mb={scoreSummary.icon.marginBottom || 0}
                         flexShrink={0}
                       />
                                         ) : (
                                               <Box
                          width={scoreSummary.icon.width || scoreSummary.icon.size || "21px"}
                          height={scoreSummary.icon.height || scoreSummary.icon.size || "21px"}
                          bg={scoreSummary.icon.bgColor || "#5F1B93"}
                          borderRadius={scoreSummary.icon.borderRadius || "8px"}
                          transform={`rotate(${scoreSummary.icon.rotation || "0deg"})`}
                          mb={scoreSummary.icon.marginBottom || 0}
                          flexShrink={0}
                        />
                     )}
                   <HeaderComponent
                     content={scoreSummary.header?.text || "Score Summary"}
                     variant={scoreSummary.header?.variant || "headline-sm-min-semibold"}
                   />
                 </HStack>
              ) : (
                <HeaderComponent
                  content={scoreSummary.header?.text || "Score Summary"}
                  variant={scoreSummary.header?.variant || "headline-sm-min-semibold"}
                />
              )}
              <Text
                fontFamily={`var(--${scoreSummary.displayText?.variant || 'body-sm-max-reg'}-font-family, Roboto)`}
                fontWeight={`var(--${scoreSummary.displayText?.variant || 'body-sm-max-reg'}-font-weight, 400)`}
                fontSize={`var(--${scoreSummary.displayText?.variant || 'body-sm-max-reg'}-font-size, 16px)`}
                lineHeight={`var(--${scoreSummary.displayText?.variant || 'body-sm-max-reg'}-line-height, 150%)`}
                letterSpacing={`var(--${scoreSummary.displayText?.variant || 'body-sm-max-reg'}-letter-spacing, 0px)`}
                color={`var(--${scoreSummary.displayText?.variant || 'body-sm-max-reg'}-color, var(--Dusky-Teal, #203433))`}
                mt={2}
              >
                {scoreSummary.displayText?.text || "Your Eating boosted your overall score the most and Physical Activity score has some room to improve. Check out the details."}
              </Text>
            </Box>
          )}

          {/* Action Plan Section */}
          {actionPlan && (
            <Box>
                             {actionPlan.icon ? (
                                   <HStack spacing={2} align="center">
                                         {actionPlan.icon.shape === 'circle' ? (
                       <Box
                         width={actionPlan.icon.size || "21px"}
                         height={actionPlan.icon.size || "21px"}
                         bg={actionPlan.icon.bgColor || "#5F1B93"}
                         borderRadius="50%"
                         transform={`rotate(${actionPlan.icon.rotation || "0deg"})`}
                         mb={actionPlan.icon.marginBottom || 0}
                         flexShrink={0}
                       />
                                         ) : actionPlan.icon.shape === 'triangle' ? (
                       <Box
                         width={0}
                         height={0}
                         borderLeft={`${parseInt(actionPlan.icon.size || "21") / 2}px solid transparent`}
                         borderRight={`${parseInt(actionPlan.icon.size || "21") / 2}px solid transparent`}
                         borderBottom={`${actionPlan.icon.size || "21px"} solid ${actionPlan.icon.bgColor || "#5F1B93"}`}
                         transform={`rotate(${actionPlan.icon.rotation || "0deg"})`}
                         mb={actionPlan.icon.marginBottom || 0}
                         flexShrink={0}
                       />
                                         ) : (
                                               <Box
                          width={actionPlan.icon.width || actionPlan.icon.size || "21px"}
                          height={actionPlan.icon.height || actionPlan.icon.size || "21px"}
                          bg={actionPlan.icon.bgColor || "#5F1B93"}
                          borderRadius={actionPlan.icon.borderRadius || "8px"}
                          transform={`rotate(${actionPlan.icon.rotation || "0deg"})`}
                          mb={actionPlan.icon.marginBottom || 0}
                          flexShrink={0}
                        />
                     )}
                   <HeaderComponent
                     content={actionPlan.header?.text || "Action Plan"}
                     variant={actionPlan.header?.variant || "headline-sm-min-semibold"}
                   />
                 </HStack>
              ) : (
                <HeaderComponent
                  content={actionPlan.header?.text || "Action Plan"}
                  variant={actionPlan.header?.variant || "headline-sm-min-semibold"}
                />
              )}
              <ListComponent
                items={actionPlan.items || [
                  "Go to your To Dos. We'll suggest Health Guides that can help your score.",
                  "Your care team will review your score and decide if other changes are needed. Feel free to call us at 855-320-5200."
                ]}
                variant={actionPlan.list?.variant || "body-sm-max-reg"}
                styleType="disc"
                markerColor="#5F1B93"
                itemColor="black"
              />
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default HealthScoreSummaryComponent;
